import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Guardian, GuardianChild, GuardianTokenResponse } from '../types/api';
import { apiClientFormEncoded } from '../services/api/client';
import { identifyUser, resetAnalytics } from '../lib/analytics';

interface GuardianAuthState {
  // State
  guardian: Guardian | null;
  accessToken: string | null;
  refreshToken: string | null;
  tokenExpiry: number | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  children: GuardianChild[];

  // Actions
  login: (username: string, password: string, organizationCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  checkTokenExpiry: () => Promise<void>;
  setGuardian: (guardian: Guardian) => void;
  setChildren: (children: GuardianChild[]) => void;
  setToken: (token: string) => void;
  setRefreshToken: (token: string) => void;
  clearAuth: () => void;
  clearError: () => void;
}

// Token storage helpers - using different keys from staff auth
const TOKEN_KEY = 'sprout_guardian_access_token';
const REFRESH_TOKEN_KEY = 'sprout_guardian_refresh_token';
const _GUARDIAN_KEY = 'sprout_guardian_user';

const getStoredToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

const setStoredToken = (token: string): void => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (error) {
    console.error('Failed to store guardian token:', error);
  }
};

const removeStoredToken = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (error) {
    console.error('Failed to remove guardian token:', error);
  }
};

const getStoredRefreshToken = (): string | null => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
};

const setStoredRefreshToken = (token: string): void => {
  try {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch (error) {
    console.error('Failed to store guardian refresh token:', error);
  }
};

const removeStoredRefreshToken = (): void => {
  try {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to remove guardian refresh token:', error);
  }
};

export const useGuardianAuthStore = create<GuardianAuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      guardian: null,
      accessToken: getStoredToken(),
      refreshToken: getStoredRefreshToken(),
      tokenExpiry: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      children: [],

      // Actions
      login: async (username: string, password: string, organizationCode?: string) => {
        set({ isLoading: true, error: null });

        try {
          // Guardian login endpoint
          const formData = new URLSearchParams();
          formData.append('username', username);
          formData.append('password', password);

          // Build URL with optional organization_code query param
          const loginUrl = organizationCode
            ? `/guardians/auth/login?organization_code=${encodeURIComponent(organizationCode)}`
            : '/guardians/auth/login';

          const response = await apiClientFormEncoded.post<GuardianTokenResponse>(
            loginUrl,
            formData
          );
          
          const { access_token, refresh_token, guardian: loginGuardian } = response.data;

          // Store tokens
          setStoredToken(access_token);
          if (refresh_token) {
            setStoredRefreshToken(refresh_token);
          }

          // Calculate token expiry (assuming 30 minute expiry)
          const tokenExpiry = Date.now() + 30 * 60 * 1000;

          set({
            guardian: loginGuardian || null,
            accessToken: access_token,
            refreshToken: refresh_token || null,
            tokenExpiry,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // If the login response didn't include guardian profile data,
          // fetch it separately so we have the name for the welcome message
          if (!loginGuardian || !loginGuardian.first_name) {
            try {
              // Lazy import to avoid circular dependency with guardian API client
              const { guardianApi } = await import('../services/api/guardian');
              const profile = await guardianApi.portal.getProfile();
              set({ guardian: profile });
            } catch {
              // Non-fatal: the user is still authenticated
            }
          }

          // Identify guardian for analytics (no-op if PostHog is not configured)
          const identifiedGuardian = get().guardian;
          if (identifiedGuardian) {
            identifyUser(identifiedGuardian.id, {
              email: identifiedGuardian.email,
              user_type: 'guardian',
            });
          }
        } catch (error: any) {
          console.error('Guardian login error:', error);
          
          set({
            isLoading: false,
            error: error.response?.data?.detail || 'Login failed',
          });
          throw error;
        }
      },

      logout: async () => {
        try {
          // Call logout endpoint (optional)
          await apiClientFormEncoded.post('/guardians/auth/logout');
        } catch (error) {
          // Ignore logout errors
          console.error('Guardian logout error:', error);
        } finally {
          // Clear local state regardless
          removeStoredToken();
          removeStoredRefreshToken();
          set({
            guardian: null,
            accessToken: null,
            refreshToken: null,
            tokenExpiry: null,
            isAuthenticated: false,
            error: null,
            children: [],
          });
          resetAnalytics();
        }
      },

      refreshAccessToken: async () => {
        const currentRefreshToken = get().refreshToken;
        if (!currentRefreshToken) {
          throw new Error('No refresh token available');
        }
        
        try {
          const response = await apiClientFormEncoded.post<GuardianTokenResponse>(
            '/guardians/auth/refresh',
            { refresh_token: currentRefreshToken }
          );
          
          const { access_token, refresh_token, guardian } = response.data;
          
          // Update tokens and guardian
          setStoredToken(access_token);
          if (refresh_token) {
            setStoredRefreshToken(refresh_token);
          }
          
          // Calculate token expiry
          const tokenExpiry = Date.now() + 30 * 60 * 1000;
          
          set({
            guardian,
            accessToken: access_token,
            refreshToken: refresh_token || currentRefreshToken,
            tokenExpiry,
            isAuthenticated: true,
            error: null,
          });
        } catch (error) {
          // Refresh failed, clear auth
          get().clearAuth();
          throw error;
        }
      },

      checkTokenExpiry: async () => {
        const state = get();
        if (!state.tokenExpiry || !state.refreshToken) {
          return;
        }
        
        // Check if token expires in less than 5 minutes
        const now = Date.now();
        const expiresIn = state.tokenExpiry - now;
        
        if (expiresIn < 5 * 60 * 1000) { // 5 minutes
          try {
            await get().refreshAccessToken();
          } catch (error) {
            console.error('Guardian token refresh failed:', error);
          }
        }
      },

      setGuardian: (guardian: Guardian) => {
        set({ guardian });
      },

      setChildren: (children: GuardianChild[]) => {
        set({ children });
      },

      setToken: (token: string) => {
        setStoredToken(token);
        set({ accessToken: token, isAuthenticated: true });
      },

      setRefreshToken: (token: string) => {
        setStoredRefreshToken(token);
        set({ refreshToken: token });
      },

      clearAuth: () => {
        removeStoredToken();
        removeStoredRefreshToken();
        set({
          guardian: null,
          accessToken: null,
          refreshToken: null,
          tokenExpiry: null,
          isAuthenticated: false,
          error: null,
          children: [],
        });
        resetAnalytics();
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'guardian-auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        guardian: state.guardian,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        children: state.children,
      }),
    }
  )
);

// Selector hooks
export const useGuardian = () => useGuardianAuthStore((state) => state.guardian);
export const useGuardianChildren = () => useGuardianAuthStore((state) => state.children);
export const useIsGuardianAuthenticated = () => useGuardianAuthStore((state) => state.isAuthenticated);
export const useGuardianAuthLoading = () => useGuardianAuthStore((state) => state.isLoading);
export const useGuardianAuthError = () => useGuardianAuthStore((state) => state.error);